import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Building2, User, Lock, Mail, Phone, MapPin, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    facility_name: '', municipality: '津市', address: '', phone: '',
    admin_name: '', admin_email: '', admin_password: '', confirm_password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.admin_password !== form.confirm_password) {
      setError('パスワードが一致しません');
      return;
    }
    if (form.admin_password.length < 6) {
      setError('パスワードは6文字以上にしてください');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        facility_name: form.facility_name,
        municipality: form.municipality,
        address: form.address,
        phone: form.phone,
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_password: form.admin_password
      });
      setUser(res.data);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-lg animate-fade-in" data-testid="register-page">
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src="/logo192.png" alt="CLSS-SaaS" className="h-10 w-10 rounded" />
            <h1 className="text-2xl font-bold tracking-tight text-foreground">CLSS-SaaS</h1>
          </div>
          <p className="text-muted-foreground text-sm">新規事業所登録</p>
        </div>

        <Card className="card-surface">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>1</div>
              <div className="h-0.5 flex-1 bg-secondary"><div className={`h-full transition-all ${step >= 2 ? 'bg-primary w-full' : 'w-0'}`} /></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>2</div>
            </div>
            <CardTitle>{step === 1 ? '事業所情報' : '管理者アカウント'}</CardTitle>
            <CardDescription>{step === 1 ? '事業所の基本情報を入力してください' : '管理者のアカウント情報を入力してください'}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--error-bg))] text-[hsl(var(--error))] text-sm mb-4" data-testid="register-error">
                <AlertCircle size={18} /><span>{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>事業所名 *</Label>
                    <div className="relative">
                      <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={form.facility_name} onChange={e => handleChange('facility_name', e.target.value)} placeholder="例: あすなろ支援センター" className="pl-10" required data-testid="facility-name-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>自治体</Label>
                      <div className="relative">
                        <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={form.municipality} onChange={e => handleChange('municipality', e.target.value)} className="pl-10" data-testid="municipality-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>電話番号</Label>
                      <div className="relative">
                        <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="059-XXX-XXXX" className="pl-10" data-testid="phone-input" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>住所</Label>
                    <Input value={form.address} onChange={e => handleChange('address', e.target.value)} placeholder="三重県津市..." data-testid="address-input" />
                  </div>
                  <Button type="button" className="w-full btn-active" onClick={() => { if (!form.facility_name) { setError('事業所名を入力してください'); return; } setError(''); setStep(2); }} data-testid="next-step-button">
                    次へ
                  </Button>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>管理者氏名 *</Label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={form.admin_name} onChange={e => handleChange('admin_name', e.target.value)} placeholder="山田 太郎" className="pl-10" required data-testid="admin-name-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>メールアドレス *</Label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input type="email" value={form.admin_email} onChange={e => handleChange('admin_email', e.target.value)} placeholder="admin@example.com" className="pl-10" required data-testid="admin-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>パスワード *</Label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input type="password" value={form.admin_password} onChange={e => handleChange('admin_password', e.target.value)} placeholder="6文字以上" className="pl-10" required data-testid="admin-password-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>パスワード確認 *</Label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input type="password" value={form.confirm_password} onChange={e => handleChange('confirm_password', e.target.value)} placeholder="もう一度入力" className="pl-10" required data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>戻る</Button>
                    <Button type="submit" className="flex-1 btn-active" disabled={loading} data-testid="register-submit-button">
                      {loading ? '登録中...' : '事業所を登録'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
            <div className="mt-6 text-center text-sm text-muted-foreground">
              既にアカウントをお持ちの場合は{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">ログイン</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
